// Compliance Assets List with Right-Hand Panel
// Full table view with Actions, Filters, and Columns management

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageWithRightPanel } from '@/components/layout/PageWithRightPanel';
import { RightPanelBase, type RightPanelTab } from '@/components/RightPanel/RightPanelBase';
import { ComplianceFiltersTab } from '@/components/compliance/ComplianceFiltersTab';
import { ComplianceAssetsColumnsTab } from '@/components/RightPanel/ComplianceAssetsColumnsTab';
import { useRightPanel } from '@/contexts/RightPanelContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useFeatureSettings } from '@/hooks/use-feature-settings';
import { useGetBackendUrl } from '@/hooks/use-get-backend-url';
import { useCompliancePermissions } from '@/hooks/use-permissions';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Shield,
  Search,
  Download,
  FileSpreadsheet,
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  Users,
  Filter,
  Columns,
  ArrowRight,
  Calendar,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  CalendarClock,
  History,
  XCircle,
  ClockAlert,
  FileX,
  HelpCircle,
  Zap,
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { exportToExcel, exportToPDF, type ExportColumn } from '@/lib/export-utils';
import type { AssetComplianceListItem } from '@/types/compliance';
import { mockAssetComplianceList } from '@/lib/mock-compliance-data';
import { getStatusConfig, getRiskBandConfig } from '@/lib/compliance-data';
import { useColumnResize } from '@/hooks/use-column-resize';
import {
  RecordInspectionPanel,
  ScheduleInspectionPanel,
  MarkNonApplicablePanel,
  DeferDueDatePanel,
  ComplianceHistoryPanel,
} from '@/components/compliance';
import {
  useRecordInspection,
  useScheduleInspection,
  useMarkNonApplicable,
  useDeferDueDate,
} from '@/hooks/use-compliance-actions';

// Default column configuration - matches AssetComplianceListItem data structure
const DEFAULT_COLUMNS = [
  { id: 'select', label: 'Select', visible: true, sortable: false },
  { id: 'assetName', label: 'Asset Name', visible: true, sortable: true },
  { id: 'category', label: 'Category', visible: true, sortable: true },
  { id: 'location', label: 'Location', visible: true, sortable: true },
  { id: 'regimeName', label: 'Regulation', visible: true, sortable: true },
  { id: 'requirementName', label: 'Requirement', visible: false, sortable: true },
  { id: 'lastEventDate', label: 'Last Check', visible: false, sortable: true },
  { id: 'nextDueDate', label: 'Next Due', visible: true, sortable: true },
  { id: 'status', label: 'Status', visible: true, sortable: true },
  { id: 'riskBand', label: 'Risk Band', visible: false, sortable: true },
  { id: 'assignedTo', label: 'Assigned To', visible: false, sortable: true },
  { id: 'lastResult', label: 'Last Result', visible: false, sortable: true },
  { id: 'contractor', label: 'Contractor', visible: false, sortable: true },
];

// Status mapping for compliance filters
const COMPLIANCE_STATUS_MAP: Record<string, string> = {
  'Compliant': 'compliant',
  'Non-Compliant': 'overdue',
  'Warning': 'due-soon',
  'Pending Review': 'pending-review',
  'Expired': 'expired',
};

// Helper function to apply URL param filters
function applyUrlParamFilters(
  assets: AssetComplianceListItem[],
  statusParam: string | null,
  regimeParam: string | null
): AssetComplianceListItem[] {
  let filtered = assets;
  if (statusParam) {
    filtered = filtered.filter(a => a.status === statusParam);
  }
  if (regimeParam) {
    filtered = filtered.filter(a => a.regimeCode === regimeParam);
  }
  return filtered;
}

// Helper function to apply table filters
function applyTableFilters(
  assets: AssetComplianceListItem[],
  tableFilters: Record<string, string | undefined>
): AssetComplianceListItem[] {
  let filtered = assets;
  if (tableFilters.status) {
    filtered = filtered.filter(a => a.status === tableFilters.status);
  }
  if (tableFilters.regimeCode) {
    filtered = filtered.filter(a => a.regimeCode === tableFilters.regimeCode);
  }
  if (tableFilters.location) {
    filtered = filtered.filter(a => a.location === tableFilters.location);
  }
  if (tableFilters.category) {
    filtered = filtered.filter(a => a.category === tableFilters.category);
  }
  if (tableFilters.riskBand) {
    filtered = filtered.filter(a => a.riskBand === tableFilters.riskBand);
  }
  return filtered;
}

// Helper function to apply compliance panel filters
function applyComplianceFilters(
  assets: AssetComplianceListItem[],
  complianceFilters: Record<string, string[]> | null
): AssetComplianceListItem[] {
  if (!complianceFilters) return assets;
  
  let filtered = assets;
  if (complianceFilters.selectedStatuses?.length > 0) {
    const mappedStatuses = complianceFilters.selectedStatuses.map((s: string) => COMPLIANCE_STATUS_MAP[s] || s.toLowerCase());
    filtered = filtered.filter(a => mappedStatuses.includes(a.status));
  }
  if (complianceFilters.selectedRiskLevels?.length > 0) {
    filtered = filtered.filter(a => a.riskBand && complianceFilters.selectedRiskLevels.includes(a.riskBand));
  }
  if (complianceFilters.selectedDepartments?.length > 0) {
    filtered = filtered.filter(a => {
      const asset = a as unknown as Record<string, unknown>;
      return asset.department && complianceFilters.selectedDepartments.includes(asset.department as string);
    });
  }
  if (complianceFilters.selectedAssignedTo?.length > 0) {
    filtered = filtered.filter(a => a.assignedTo && complianceFilters.selectedAssignedTo.includes(a.assignedTo));
  }
  return filtered;
}

// Helper function to apply quick filters
function applyQuickFilter(
  assets: AssetComplianceListItem[],
  activeQuickFilter: string | null
): AssetComplianceListItem[] {
  if (!activeQuickFilter) return assets;
  
  switch (activeQuickFilter) {
    case 'overdue':
      return assets.filter(a => a.status === 'overdue');
    case 'dueThisWeek':
      return assets.filter(a => a.daysUntilDue !== undefined && a.daysUntilDue >= 0 && a.daysUntilDue <= 7);
    case 'dueThisMonth':
      return assets.filter(a => a.daysUntilDue !== undefined && a.daysUntilDue >= 0 && a.daysUntilDue <= 30);
    case 'highRisk':
      return assets.filter(a => a.riskBand === 'High' || a.riskBand === 'Critical');
    case 'unassigned':
      return assets.filter(a => !a.assignedTo || a.assignedTo === '');
    default:
      return assets;
  }
}

// Helper function to apply search query filter
function applySearchFilter(
  assets: AssetComplianceListItem[],
  searchQuery: string
): AssetComplianceListItem[] {
  if (!searchQuery) return assets;
  
  const query = searchQuery.toLowerCase();
  return assets.filter(a =>
    a.assetName.toLowerCase().includes(query) ||
    a.assetId.toLowerCase().includes(query) ||
    a.regimeName.toLowerCase().includes(query)
  );
}

// Initial column widths
const INITIAL_COLUMN_WIDTHS = {
  select: 80,
  assetName: 250,
  category: 150,
  location: 200,
  regimeName: 200,
  requirementName: 220,
  lastEventDate: 140,
  nextDueDate: 180,
  status: 140,
  riskBand: 120,
  assignedTo: 180,
  lastResult: 140,
  contractor: 200,
};

// Action Card Component
const ActionCard = ({
  icon: Icon,
  title,
  description,
  onClick,
  variant,
  disabled
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  onClick?: () => void;
  variant: 'view' | 'action' | 'delete';
  disabled?: boolean;
}) => {
  const variantStyles = {
    view: {
      container: 'bg-blue-50 dark:bg-blue-950/30 hover:bg-blue-100 dark:hover:bg-blue-950/50',
      icon: 'text-blue-600 dark:text-blue-400',
      iconBg: 'bg-blue-100 dark:bg-blue-900/50'
    },
    action: {
      container: 'bg-primary/5 dark:bg-primary/10 hover:bg-primary/10 dark:hover:bg-primary/15',
      icon: 'text-primary dark:text-primary',
      iconBg: 'bg-primary/10 dark:bg-primary/20'
    },
    delete: {
      container: 'bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50',
      icon: 'text-red-600 dark:text-red-400',
      iconBg: 'bg-red-100 dark:bg-red-900/50'
    }
  };

  const styles = variantStyles[variant];

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'w-full p-[5px] rounded-lg transition-all duration-200 text-left group cursor-pointer',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        styles.container
      )}
    >
      <div className="flex items-center gap-3">
        <div className={cn(
          'flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110',
          styles.iconBg
        )}>
          <Icon className={cn('h-6 w-6', styles.icon)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold text-sm text-foreground">
              {title}
            </h3>
            <ArrowRight className={cn('h-4 w-4 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity', styles.icon)} />
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
            {description}
          </p>
        </div>
      </div>
    </button>
  );
};

// Sort Icon Component - extracted outside parent component
const SortIcon = ({ columnKey, sortConfig }: { columnKey: string; sortConfig: { key: string | null; direction: 'asc' | 'desc' | null } }) => {
  if (sortConfig.key !== columnKey) {
    return null;
  }
  return sortConfig.direction === 'asc' ? (
    <ArrowUp className="ml-2 h-4 w-4" />
  ) : (
    <ArrowDown className="ml-2 h-4 w-4" />
  );
};

export default function ComplianceAssetsPage(): React.ReactElement {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { modules } = useFeatureSettings();
  const complianceModule = modules.compliance;
  const isDemoMode = complianceModule?.mode === 'demo';
  const backendUrl = useGetBackendUrl();
  const { canManage: canManageCompliance } = useCompliancePermissions();
  
  const {
    selectedRowIds,
    setSelectedRowIds,
    visibleColumns,
    setVisibleColumns,
    columnOrder,
    setColumnOrder,
    tableFilters,
    setIsExpanded,
    setActiveTab,
    isPinned,
  } = useRightPanel();

  const [loading, setLoading] = useState(true);
  const [assetList, setAssetList] = useState<AssetComplianceListItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [activeQuickFilter, setActiveQuickFilter] = useState<string | null>(null);
  const [complianceFilters, setComplianceFilters] = useState<any>({
    selectedStatuses: ['Non-Compliant', 'Warning'],
    selectedTypes: [],
    selectedRiskLevels: [],
    selectedDepartments: [],
    selectedAssignedTo: [],
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: 'asc' | 'desc' | null;
  }>({
    key: 'asset',
    direction: 'asc',
  });

  // Modal state management
  const [activeModal, setActiveModal] = useState<{
    type: 'record' | 'schedule' | 'history' | 'nonApplicable' | 'defer' | null;
    item: AssetComplianceListItem | null;
  }>({ type: null, item: null });

  // Quick filters configuration
  const quickFilters = [
    { 
      id: 'overdue', 
      name: 'Overdue', 
      icon: AlertCircle, 
      bgColor: 'bg-red-50 hover:bg-red-100',
      textColor: 'text-red-700',
      borderColor: 'border-red-200',
      activeBgColor: 'bg-red-100',
      activeTextColor: 'text-red-800',
      activeBorderColor: 'border-red-300'
    },
    { 
      id: 'dueThisWeek', 
      name: 'Due This Week', 
      icon: Clock, 
      bgColor: 'bg-orange-50 hover:bg-orange-100',
      textColor: 'text-orange-700',
      borderColor: 'border-orange-200',
      activeBgColor: 'bg-orange-100',
      activeTextColor: 'text-orange-800',
      activeBorderColor: 'border-orange-300'
    },
    { 
      id: 'dueThisMonth', 
      name: 'Due This Month', 
      icon: Calendar,
      bgColor: 'bg-blue-50 hover:bg-blue-100',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-200',
      activeBgColor: 'bg-blue-100',
      activeTextColor: 'text-blue-800',
      activeBorderColor: 'border-blue-300'
    },
    { 
      id: 'highRisk', 
      name: 'High Risk', 
      icon: Shield, 
      bgColor: 'bg-purple-50 hover:bg-purple-100',
      textColor: 'text-purple-700',
      borderColor: 'border-purple-200',
      activeBgColor: 'bg-purple-100',
      activeTextColor: 'text-purple-800',
      activeBorderColor: 'border-purple-300'
    },
    { 
      id: 'unassigned', 
      name: 'Unassigned', 
      icon: Users,
      bgColor: 'bg-gray-50 hover:bg-gray-100',
      textColor: 'text-gray-700',
      borderColor: 'border-gray-200',
      activeBgColor: 'bg-gray-100',
      activeTextColor: 'text-gray-800',
      activeBorderColor: 'border-gray-300'
    },
  ];

  const handleQuickFilterToggle = (filterId: string) => {
    if (activeQuickFilter === filterId) {
      setActiveQuickFilter(null);
    } else {
      setActiveQuickFilter(filterId);
    }
  };

  // Compliance action hooks
  const recordInspection = useRecordInspection();
  const scheduleInspection = useScheduleInspection();
  const markNonApplicable = useMarkNonApplicable();
  const deferDueDate = useDeferDueDate();

  // Column resize hook
  const { columnWidths, handleMouseDown } = useColumnResize(
    INITIAL_COLUMN_WIDTHS,
    ['select'] // Fixed columns that cannot be resized
  );

  const pageSize = 25;

  // Initialize columns
  useEffect(() => {
    if (!isInitialized) {
      if (visibleColumns.length === 0) {
        const defaultVisible = DEFAULT_COLUMNS
          .filter(col => col.visible)
          .map(col => col.id);
        setVisibleColumns(defaultVisible);
      }
      if (columnOrder.length === 0) {
        setColumnOrder(DEFAULT_COLUMNS.map(col => col.id));
      }
      setIsInitialized(true);
    }
  }, [isInitialized, visibleColumns.length, columnOrder.length, setVisibleColumns, setColumnOrder]);

  // Ensure we always have visible columns (fallback safety)
  const effectiveVisibleColumns = useMemo(() => {
    if (visibleColumns.length > 0) {
      return visibleColumns;
    }
    // Fallback to default visible columns if none are set
    return DEFAULT_COLUMNS.filter(col => col.visible).map(col => col.id);
  }, [visibleColumns]);

  // Sync URL parameters with filters on mount
  useEffect(() => {
    const statusParam = searchParams.get('status');
    const regimeParam = searchParams.get('regime');
    
    if (statusParam || regimeParam) {
      // Map URL status param to filter format
      const statusMap: Record<string, string> = {
        'compliant': 'Compliant',
        'overdue': 'Non-Compliant',
        'due-soon': 'Warning',
        'pending-review': 'Pending Review',
        'expired': 'Expired',
      };
      
      const mappedStatus = statusParam ? statusMap[statusParam] || statusParam : null;
      
      // Update compliance filters to show URL params in the filter UI
      setComplianceFilters((prev: any) => ({
        ...prev,
        selectedStatuses: mappedStatus ? [mappedStatus] : prev.selectedStatuses,
      }));
    }
  }, [searchParams]);

  // Load data
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        
        if (isDemoMode) {
          // Demo mode: use mock data
          await new Promise((resolve) => setTimeout(resolve, 300));
          setAssetList(mockAssetComplianceList);
        } else {
          // Live mode: fetch from API
          try {
            const response = await fetch(`${backendUrl}/api/Compliance/asset-compliances`);
            
            if (response.ok) {
              const data = await response.json();
              console.log('Fetched compliance assets from API:', data);
              setAssetList(data);
            } else {
              console.error('API returned error status:', response.status);
              setAssetList([]);
            }
          } catch (apiError) {
            console.error('Failed to load compliance assets from API:', apiError);
            // In live mode, don't fall back to demo data
            setAssetList([]);
          }
        }
      } catch (error) {
        console.error('Failed to load compliance assets:', error);
        if (!isDemoMode) {
          // In live mode, show empty state on error
          setAssetList([]);
        }
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [isDemoMode, backendUrl]);

  // Apply filters from URL params, table filters, and compliance filters
  const filteredAssets = useMemo(() => {
    const statusParam = searchParams.get('status');
    const regimeParam = searchParams.get('regime');

    let filtered = [...assetList];
    filtered = applyUrlParamFilters(filtered, statusParam, regimeParam);
    filtered = applyTableFilters(filtered, tableFilters);
    filtered = applyComplianceFilters(filtered, complianceFilters);
    filtered = applyQuickFilter(filtered, activeQuickFilter);
    filtered = applySearchFilter(filtered, searchQuery);

    return filtered;
  }, [assetList, searchParams, tableFilters, searchQuery, complianceFilters, activeQuickFilter]);

  // Apply sorting
  const sortedAssets = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return filteredAssets;

    return [...filteredAssets].sort((a: any, b: any) => {
      let aValue = a[sortConfig.key!];
      let bValue = b[sortConfig.key!];

      // Handle null/undefined
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // String comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Numeric comparison
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Date comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const aDate = new Date(aValue);
        const bDate = new Date(bValue);
        if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
          return sortConfig.direction === 'asc'
            ? aDate.getTime() - bDate.getTime()
            : bDate.getTime() - aDate.getTime();
        }
      }

      return 0;
    });
  }, [filteredAssets, sortConfig]);

  // Apply pagination
  const totalPages = Math.ceil(sortedAssets.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedAssets = sortedAssets.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, complianceFilters, tableFilters]);

  // Sort handler - cycles through: unsorted → asc → desc → unsorted
  const handleSort = useCallback((key: string) => {
    setSortConfig((prevConfig) => {
      // If clicking a different column, start with asc
      if (prevConfig.key !== key) {
        return { key, direction: 'asc' };
      }
      // If same column, cycle through: asc → desc → unsorted
      if (prevConfig.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      if (prevConfig.direction === 'desc') {
        return { key: null, direction: null };
      }
      // If unsorted, start with asc
      return { key, direction: 'asc' };
    });
  }, []);

  // Handle row selection
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      const allIds = new Set(paginatedAssets.map(a => a.assetId));
      setSelectedRowIds(allIds);
      if (allIds.size > 0 && !isPinned) {
        setIsExpanded(true);
        setActiveTab('actions');
      }
    } else {
      setSelectedRowIds(new Set());
      if (!isPinned) {
        setIsExpanded(false);
      }
    }
  }, [paginatedAssets, isPinned, setSelectedRowIds, setIsExpanded, setActiveTab]);

  const handleSelectRow = useCallback((assetId: string, checked: boolean) => {
    setSelectedRowIds(prev => {
      // Only allow single selection
      if (checked) {
        const newSet = new Set([assetId]);
        if (!isPinned) {
          setIsExpanded(true);
          setActiveTab('actions');
        }
        return newSet;
      } else {
        if (!isPinned) {
          setIsExpanded(false);
        }
        return new Set();
      }
    });
  }, [setSelectedRowIds, setIsExpanded, setActiveTab, isPinned]);

  const handleAssetClick = (assetId: string) => {
    navigate(`/assets?id=${assetId}`);
  };

  // Page number generation helper
  const generatePageNumbers = (currentPage: number, totalPages: number): (number | string)[] => {
    const pageNumbers: (number | string)[] = [];
    const maxVisiblePages = 7;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);
      
      if (currentPage > 3) {
        pageNumbers.push('...');
      }
      
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        pageNumbers.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        pageNumbers.push('...');
      }
      
      pageNumbers.push(totalPages);
    }
    
    return pageNumbers;
  };

  // Actions Tab Component - Define handlers with useCallback to prevent recreation
  const hasSelection = selectedRowIds.size > 0;
  const selectedCount = selectedRowIds.size;
  const isSingleSelection = selectedCount === 1;

  // Get the selected asset if only one is selected
  const selectedAsset = useMemo(() => {
    if (isSingleSelection) {
      return filteredAssets.find(asset => selectedRowIds.has(asset.assetId));
    }
    return null;
  }, [isSingleSelection, filteredAssets, selectedRowIds]);

  const handleExportCSV = useCallback(() => {
    const dataToExport = hasSelection 
      ? filteredAssets.filter(asset => selectedRowIds.has(asset.assetId))
      : filteredAssets;
    
    const headers = ['Asset Name', 'Asset ID', 'Category', 'Location', 'Regulation', 'Requirement', 'Last Check', 'Next Due', 'Days Until Due', 'Status', 'Risk Band', 'Assigned To', 'Last Result', 'Contractor'];
    const rows = dataToExport.map(asset => [asset.assetName, asset.assetId, asset.category, asset.location, asset.regimeName, asset.requirementName || '', asset.lastEventDate || '', asset.nextDueDate || '', asset.daysUntilDue?.toString() || '', asset.status, asset.riskBand || '', asset.assignedTo || '', asset.lastResult || '', asset.contractor || '']);
    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `compliance-assets-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [hasSelection, filteredAssets, selectedRowIds]);

  const handleExportExcel = useCallback(() => {
    const dataToExport = hasSelection 
      ? filteredAssets.filter(asset => selectedRowIds.has(asset.assetId))
      : filteredAssets;
    
    const columns: ExportColumn<AssetComplianceListItem>[] = [
      { key: 'assetName', label: 'Asset Name' },
      { key: 'assetId', label: 'Asset ID' },
      { key: 'category', label: 'Category' },
      { key: 'location', label: 'Location' },
      { key: 'regimeName', label: 'Regulation' },
      { key: 'requirementName', label: 'Requirement', format: (v) => v || '' },
      { key: 'lastEventDate', label: 'Last Check', format: (v) => v || '' },
      { key: 'nextDueDate', label: 'Next Due', format: (v) => v || '' },
      { key: 'daysUntilDue', label: 'Days Until Due', format: (v) => v?.toString() || '' },
      { key: 'status', label: 'Status' },
      { key: 'riskBand', label: 'Risk Band', format: (v) => v || '' },
      { key: 'assignedTo', label: 'Assigned To', format: (v) => v || '' },
      { key: 'lastResult', label: 'Last Result', format: (v) => v || '' },
      { key: 'contractor', label: 'Contractor', format: (v) => v || '' },
    ];
    
    exportToExcel({
      data: dataToExport,
      columns,
      filename: `compliance-assets-${format(new Date(), 'yyyy-MM-dd')}.xlsx`,
      sheetName: 'Compliance Assets',
    });
  }, [hasSelection, filteredAssets, selectedRowIds]);

  const handleExportPDF = useCallback(() => {
    const dataToExport = hasSelection 
      ? filteredAssets.filter(asset => selectedRowIds.has(asset.assetId))
      : filteredAssets;
    
    const columns: ExportColumn<AssetComplianceListItem>[] = [
      { key: 'assetName', label: 'Asset Name' },
      { key: 'category', label: 'Category' },
      { key: 'location', label: 'Location' },
      { key: 'regimeName', label: 'Regulation' },
      { key: 'nextDueDate', label: 'Next Due', format: (v) => v || '' },
      { key: 'status', label: 'Status' },
      { key: 'riskBand', label: 'Risk Band', format: (v) => v || '' },
      { key: 'assignedTo', label: 'Assigned To', format: (v) => v || '' },
    ];

    // Build filters for PDF display
    const appliedFilters: Record<string, string> = {};
    if (complianceFilters.status?.length) {
      appliedFilters.status = complianceFilters.status.join(', ');
    }
    if (complianceFilters.regimes?.length) {
      appliedFilters.regimes = complianceFilters.regimes.join(', ');
    }
    
    exportToPDF({
      data: dataToExport,
      columns,
      filename: `compliance-assets-${format(new Date(), 'yyyy-MM-dd')}.pdf`,
      title: 'Compliance Assets Report',
      subtitle: 'Asset Management System',
      filters: appliedFilters,
      orientation: 'landscape',
    });
  }, [hasSelection, filteredAssets, selectedRowIds, complianceFilters]);

  const handleRecordInspection = useCallback(() => {
    console.log('handleRecordInspection called', { isSingleSelection, selectedAsset });
    if (isSingleSelection && selectedAsset) {
      console.log('Opening record modal for', selectedAsset);
      setActiveModal({ type: 'record', item: selectedAsset });
    } else {
      // TODO: Implement bulk record inspection
      console.log('Bulk record inspection', Array.from(selectedRowIds));
    }
  }, [isSingleSelection, selectedAsset, selectedRowIds]);

  const handleScheduleInspection = useCallback(() => {
    console.log('handleScheduleInspection called', { isSingleSelection, selectedAsset });
    if (isSingleSelection && selectedAsset) {
      console.log('Opening schedule modal for', selectedAsset);
      setActiveModal({ type: 'schedule', item: selectedAsset });
    } else {
      // TODO: Implement bulk schedule inspection
      console.log('Bulk schedule inspection', Array.from(selectedRowIds));
    }
  }, [isSingleSelection, selectedAsset, selectedRowIds]);

  const handleMarkNonApplicable = useCallback(() => {
    console.log('handleMarkNonApplicable called', { isSingleSelection, selectedAsset });
    if (isSingleSelection && selectedAsset) {
      console.log('Opening non-applicable modal for', selectedAsset);
      setActiveModal({ type: 'nonApplicable', item: selectedAsset });
    } else {
      // TODO: Implement bulk mark non-applicable
      console.log('Bulk mark non-applicable', Array.from(selectedRowIds));
    }
  }, [isSingleSelection, selectedAsset, selectedRowIds]);

  const handleDeferDueDate = useCallback(() => {
    console.log('handleDeferDueDate called', { isSingleSelection, selectedAsset });
    if (isSingleSelection && selectedAsset) {
      console.log('Opening defer modal for', selectedAsset);
      setActiveModal({ type: 'defer', item: selectedAsset });
    } else {
      // TODO: Implement bulk defer due date
      console.log('Bulk defer due date', Array.from(selectedRowIds));
    }
  }, [isSingleSelection, selectedAsset, selectedRowIds]);

  const handleViewHistory = useCallback(() => {
    console.log('handleViewHistory called', { isSingleSelection, selectedAsset });
    if (isSingleSelection && selectedAsset) {
      console.log('Opening history panel for', selectedAsset);
      setActiveModal({ type: 'history', item: selectedAsset });
    }
  }, [isSingleSelection, selectedAsset]);

  const ActionsTab = useCallback(() => {

    return (
      <div className="p-4 space-y-3">
        {/* Always Visible Actions */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
            Export
          </h4>
          <ActionCard
            icon={Download}
            title="Export to CSV"
            description="Download all compliance data as CSV file"
            variant="action"
            onClick={handleExportCSV}
          />
          <ActionCard
            icon={FileSpreadsheet}
            title="Export to Excel"
            description="Generate Excel spreadsheet with all data"
            variant="action"
            onClick={handleExportExcel}
          />
          <ActionCard
            icon={FileText}
            title="Export to PDF"
            description="Generate professional PDF report"
            variant="action"
            onClick={handleExportPDF}
          />
        </div>

        {/* Selection-Dependent Actions */}
        {!hasSelection ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center border-t pt-4">
            <p className="text-xs text-muted-foreground">
              Select one or more assets to see additional actions
            </p>
          </div>
        ) : (
          <div className="space-y-2 border-t pt-3">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
              Compliance Actions ({selectedCount} {selectedCount === 1 ? 'item' : 'items'})
            </h4>
            {canManageCompliance && (
              <ActionCard
                icon={ClipboardCheck}
                title="Record Inspection"
                description={isSingleSelection ? 'Log completed inspection' : `Log inspections for ${selectedCount} items`}
                variant="action"
                onClick={handleRecordInspection}
              />
            )}
            {canManageCompliance && (
              <ActionCard
                icon={CalendarClock}
                title="Schedule Inspection"
                description={isSingleSelection ? 'Set next inspection date' : `Schedule inspections for ${selectedCount} items`}
                variant="action"
                onClick={handleScheduleInspection}
              />
            )}
            {isSingleSelection && (
              <ActionCard
                icon={History}
                title="View History"
                description="View compliance history timeline"
                variant="action"
                onClick={handleViewHistory}
              />
            )}
            {canManageCompliance && (
              <ActionCard
                icon={XCircle}
                title="Mark Non-Applicable"
                description={isSingleSelection ? 'Exempt from compliance' : `Exempt ${selectedCount} items from compliance`}
                variant="action"
                onClick={handleMarkNonApplicable}
              />
            )}
            {canManageCompliance && (
              <ActionCard
                icon={ClockAlert}
                title="Defer Due Date"
                description={isSingleSelection ? 'Postpone deadline' : `Postpone deadlines for ${selectedCount} items`}
                variant="action"
                onClick={handleDeferDueDate}
              />
            )}
          </div>
        )}
      </div>
    );
  }, [hasSelection, selectedCount, isSingleSelection, handleExportCSV, handleExportExcel, handleRecordInspection, handleScheduleInspection, handleViewHistory, handleMarkNonApplicable, handleDeferDueDate]);

  // Define right panel tabs - useMemo to prevent recreation
  const rightPanelTabs: RightPanelTab[] = useMemo(() => [
    {
      id: 'actions',
      label: 'Actions',
      icon: Zap,
      content: <ActionsTab />,
      showBadge: true,
    },
    {
      id: 'filters',
      label: 'Filters',
      icon: Filter,
      content: <ComplianceFiltersTab onFiltersChange={setComplianceFilters} />,
    },
    {
      id: 'columns',
      label: 'Columns',
      icon: Columns,
      content: <ComplianceAssetsColumnsTab />,
      badgeCount: visibleColumns.length,
    },
  ], [ActionsTab, setComplianceFilters, visibleColumns.length]);

  // Helper function for days until due
  const getDaysUntilDueText = (daysUntilDue: number): string => {
    if (daysUntilDue < 0) return `${Math.abs(daysUntilDue)}d overdue`;
    if (daysUntilDue === 0) return 'Due today';
    return `${daysUntilDue}d remaining`;
  };

  const getDaysUntilDueColorClass = (daysUntilDue: number): string => {
    if (daysUntilDue < 0) return 'text-red-600';
    if (daysUntilDue <= 7) return 'text-yellow-600';
    return 'text-muted-foreground';
  };



  return (
    <PageWithRightPanel>
      <div className="flex h-full flex-col">
        {/* Page Header */}
        <div className="bg-card border-b">
          <div className="p-6">
            <div className="flex flex-col items-start gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                  <Shield className="h-6 w-6 text-primary" />
                  Compliance Management
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Physical asset compliance management for UK HSE & British Standards
                </p>
              </div>
            </div>
          </div>
        </div>



        {/* Content - Table Container */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {loading || !isInitialized ? (
            <div className="flex-1 overflow-auto p-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-96 w-full" />
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <>
          {/* Search Bar - Fixed */}
          <div className="border-b bg-card px-6 py-4 flex-shrink-0">
            <div className="flex items-center gap-3">
              {selectedRowIds.size > 0 && (
                <Badge variant="default" className="h-6 px-3">
                  {selectedRowIds.size} selected
                </Badge>
              )}
              <div className="relative w-80">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search assets, IDs, regulations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-white dark:bg-slate-950"
                />
              </div>

              {/* Quick Filters */}
              <div className="flex items-center gap-2 flex-1 overflow-x-auto">
                {quickFilters.map((filter) => {
                  const Icon = filter.icon;
                  const isActive = activeQuickFilter === filter.id;
                  return (
                    <Badge
                      key={filter.id}
                      variant="outline"
                      className={cn(
                        'cursor-pointer transition-colors px-3 py-1.5 whitespace-nowrap',
                        isActive
                          ? `${filter.activeBgColor} ${filter.activeTextColor} ${filter.activeBorderColor}`
                          : `${filter.bgColor} ${filter.textColor} ${filter.borderColor}`
                      )}
                      onClick={() => handleQuickFilterToggle(filter.id)}
                    >
                      <Icon className="mr-1.5 h-3.5 w-3.5" />
                      {filter.name}
                    </Badge>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Table - Scrollable */}
          <div className="flex-1 overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
                <TableRow className="border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                  {/* Checkbox Column */}
                  {effectiveVisibleColumns.includes('select') && (
                    <TableHead className="h-12 font-semibold relative" style={{ width: `${columnWidths.select}px` }}>
                      <div className="flex items-center justify-center px-3">
                        <Checkbox
                          checked={false}
                          disabled={true}
                          className="opacity-0"
                        />
                      </div>
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 h-6">
                        <div className="w-px h-full bg-slate-300 dark:bg-slate-600" />
                      </div>
                    </TableHead>
                  )}

                  {/* Asset Name Column */}
                  {effectiveVisibleColumns.includes('assetName') && (
                    <TableHead className="h-12 font-semibold relative" style={{ width: `${columnWidths.assetName}px` }}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-ml-3 h-8 hover:bg-slate-200 dark:hover:bg-slate-700"
                        onClick={() => handleSort('assetName')}
                      >
                        Asset Name
                        <SortIcon columnKey="assetName" sortConfig={sortConfig} />
                      </Button>
                      <div
                        className="absolute right-0 top-1/2 -translate-y-1/2 h-6 w-1 cursor-col-resize hover:bg-primary/20 group"
                        onMouseDown={(e) => handleMouseDown('assetName', e.clientX)}
                      >
                        <div className="absolute right-0 top-0 bottom-0 w-px bg-slate-300 dark:bg-slate-600 group-hover:bg-primary" />
                      </div>
                    </TableHead>
                  )}

                  {/* Other columns */}
                  {effectiveVisibleColumns.includes('category') && <TableHead className="h-12 font-semibold" style={{ width: `${columnWidths.category}px` }}>Category</TableHead>}
                  {effectiveVisibleColumns.includes('location') && <TableHead className="h-12 font-semibold" style={{ width: `${columnWidths.location}px` }}>Location</TableHead>}
                  {effectiveVisibleColumns.includes('regimeName') && <TableHead className="h-12 font-semibold" style={{ width: `${columnWidths.regimeName}px` }}>Regulation</TableHead>}
                  {effectiveVisibleColumns.includes('requirementName') && <TableHead className="h-12 font-semibold" style={{ width: `${columnWidths.requirementName}px` }}>Requirement</TableHead>}
                  {effectiveVisibleColumns.includes('lastEventDate') && <TableHead className="h-12 font-semibold" style={{ width: `${columnWidths.lastEventDate}px` }}>Last Check</TableHead>}
                  {effectiveVisibleColumns.includes('nextDueDate') && <TableHead className="h-12 font-semibold" style={{ width: `${columnWidths.nextDueDate}px` }}>Next Due</TableHead>}
                  {effectiveVisibleColumns.includes('status') && <TableHead className="h-12 font-semibold" style={{ width: `${columnWidths.status}px` }}>Status</TableHead>}
                  {effectiveVisibleColumns.includes('riskBand') && <TableHead className="h-12 font-semibold" style={{ width: `${columnWidths.riskBand}px` }}>Risk Band</TableHead>}
                  {effectiveVisibleColumns.includes('assignedTo') && <TableHead className="h-12 font-semibold" style={{ width: `${columnWidths.assignedTo}px` }}>Assigned To</TableHead>}
                  {effectiveVisibleColumns.includes('lastResult') && <TableHead className="h-12 font-semibold" style={{ width: `${columnWidths.lastResult}px` }}>Last Result</TableHead>}
                  {effectiveVisibleColumns.includes('contractor') && <TableHead className="h-12 font-semibold" style={{ width: `${columnWidths.contractor}px` }}>Contractor</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedAssets.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={effectiveVisibleColumns.length}
                      className="h-96"
                    >
                      <div className="flex flex-col items-center justify-center space-y-4 py-12">
                        <div className="rounded-full bg-slate-100 dark:bg-slate-800 p-6">
                          <FileX className="h-12 w-12 text-slate-400 dark:text-slate-500" />
                        </div>
                        <div className="space-y-2 text-center">
                          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                            No assets found
                          </h3>
                          <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                            {searchQuery || Object.keys(complianceFilters).some(k => complianceFilters[k]?.length > 0)
                              ? "Try adjusting your search or filters to find what you're looking for"
                              : "No compliance data available yet"}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedAssets.map((asset, index) => {
                          const statusConfig = getStatusConfig(asset.status);
                          const riskConfig = asset.riskBand ? getRiskBandConfig(asset.riskBand) : null;
                          const StatusIcon = statusConfig.icon === 'CheckCircle' ? CheckCircle :
                                            statusConfig.icon === 'Clock' ? Clock :
                                            statusConfig.icon === 'AlertCircle' ? AlertCircle : HelpCircle;

                          return (
                            <TableRow 
                              key={asset.assetId}
                              className={cn(
                                index % 2 === 0 
                                  ? 'bg-white dark:bg-slate-950' 
                                  : 'bg-slate-50/50 dark:bg-slate-900/50',
                                'hover:bg-slate-100 dark:hover:bg-slate-800',
                                'transition-colors',
                                'border-b border-slate-100 dark:border-slate-800'
                              )}
                            >
                              {effectiveVisibleColumns.includes('select') && (
                                <TableCell style={{ width: `${columnWidths.select}px` }}>
                                  <div className="flex items-center justify-center">
                                    <Checkbox
                                      checked={selectedRowIds.has(asset.assetId)}
                                      onCheckedChange={(checked) => handleSelectRow(asset.assetId, checked as boolean)}
                                    />
                                  </div>
                                </TableCell>
                              )}
                              {effectiveVisibleColumns.includes('assetName') && (
                                <TableCell 
                                  style={{ width: `${columnWidths.assetName}px` }}
                                  className="px-4 py-3 text-slate-700 dark:text-slate-300"
                                >
                                  <button
                                    onClick={() => handleAssetClick(asset.assetId)}
                                    className="text-primary hover:text-primary/80 font-medium hover:underline hover:underline-offset-4 transition-all cursor-pointer inline-flex items-center gap-1 group text-left"
                                  >
                                    <div>
                                      <div>{asset.assetName}</div>
                                      <div className="text-xs text-muted-foreground">{asset.assetId}</div>
                                    </div>
                                    <span className="opacity-0 group-hover:opacity-100 transition-opacity">→</span>
                                  </button>
                                </TableCell>
                              )}
                              {effectiveVisibleColumns.includes('category') && (
                                <TableCell 
                                  style={{ width: `${columnWidths.category}px` }}
                                  className="px-4 py-3"
                                >
                                  <Badge variant="outline" className="font-normal">
                                    {asset.category}
                                  </Badge>
                                </TableCell>
                              )}
                              {effectiveVisibleColumns.includes('location') && (
                                <TableCell 
                                  style={{ width: `${columnWidths.location}px` }}
                                  className="px-4 py-3 text-slate-700 dark:text-slate-300"
                                >
                                  {asset.location}
                                </TableCell>
                              )}
                              {effectiveVisibleColumns.includes('regimeName') && (
                                <TableCell 
                                  style={{ width: `${columnWidths.regimeName}px` }}
                                  className="px-4 py-3 text-slate-700 dark:text-slate-300"
                                >
                                  <button
                                    onClick={() => navigate(`/compliance?regime=${asset.regimeCode}`)}
                                    className="text-primary hover:text-primary/80 font-medium hover:underline hover:underline-offset-4 transition-all cursor-pointer inline-flex items-center gap-1 group text-left"
                                  >
                                    <div>
                                      <div className="font-semibold">{asset.regimeCode}</div>
                                      <div className="text-xs text-muted-foreground">{asset.regimeName}</div>
                                    </div>
                                    <span className="opacity-0 group-hover:opacity-100 transition-opacity">→</span>
                                  </button>
                                </TableCell>
                              )}
                              {effectiveVisibleColumns.includes('requirementName') && (
                                <TableCell 
                                  style={{ width: `${columnWidths.requirementName}px` }}
                                  className="px-4 py-3 text-slate-700 dark:text-slate-300"
                                >
                                  {asset.requirementName || <span className="text-muted-foreground">-</span>}
                                </TableCell>
                              )}
                              {effectiveVisibleColumns.includes('lastEventDate') && (
                                <TableCell 
                                  style={{ width: `${columnWidths.lastEventDate}px` }}
                                  className="px-4 py-3 text-slate-700 dark:text-slate-300 tabular-nums"
                                >
                                  {asset.lastEventDate ? format(new Date(asset.lastEventDate), 'dd/MM/yyyy') : (
                                    <span className="text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                              )}
                              {effectiveVisibleColumns.includes('nextDueDate') && (
                                <TableCell 
                                  style={{ width: `${columnWidths.nextDueDate}px` }}
                                  className="px-4 py-3 text-slate-700 dark:text-slate-300 tabular-nums"
                                >
                                  {asset.nextDueDate ? (
                                    <div>
                                      <div className="text-sm font-medium">
                                        {format(new Date(asset.nextDueDate), 'dd/MM/yyyy')}
                                      </div>
                                      {asset.daysUntilDue !== undefined && (
                                        <div className={cn(
                                          "text-xs font-medium",
                                          getDaysUntilDueColorClass(asset.daysUntilDue)
                                        )}>
                                          {getDaysUntilDueText(asset.daysUntilDue)}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                              )}
                              {effectiveVisibleColumns.includes('status') && (
                                <TableCell 
                                  style={{ width: `${columnWidths.status}px` }}
                                  className="px-4 py-3"
                                >
                                  <Badge className={statusConfig.className}>
                                    <StatusIcon className="mr-1 h-3 w-3" />
                                    {statusConfig.label}
                                  </Badge>
                                </TableCell>
                              )}
                              {effectiveVisibleColumns.includes('riskBand') && (
                                <TableCell 
                                  style={{ width: `${columnWidths.riskBand}px` }}
                                  className="px-4 py-3"
                                >
                                  {riskConfig ? (
                                    <Badge variant="outline" className={riskConfig.className}>
                                      {riskConfig.label}
                                    </Badge>
                                  ) : (
                                    <span className="text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                              )}
                              {effectiveVisibleColumns.includes('assignedTo') && (
                                <TableCell 
                                  style={{ width: `${columnWidths.assignedTo}px` }}
                                  className="px-4 py-3 text-slate-700 dark:text-slate-300"
                                >
                                  {asset.assignedTo ? (
                                    <div className="flex items-center gap-1">
                                      <Users className="h-3 w-3 text-muted-foreground" />
                                      <span>{asset.assignedTo}</span>
                                    </div>
                                  ) : (
                                    <span className="text-muted-foreground">Unassigned</span>
                                  )}
                                </TableCell>
                              )}
                              {effectiveVisibleColumns.includes('lastResult') && (
                                <TableCell 
                                  style={{ width: `${columnWidths.lastResult}px` }}
                                  className="px-4 py-3"
                                >
                                  {asset.lastResult ? (
                                    <Badge 
                                      variant={asset.lastResult === 'PASS' ? 'default' : 'destructive'}
                                      className="font-normal"
                                    >
                                      {asset.lastResult}
                                    </Badge>
                                  ) : (
                                    <span className="text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                              )}
                              {effectiveVisibleColumns.includes('contractor') && (
                                <TableCell 
                                  style={{ width: `${columnWidths.contractor}px` }}
                                  className="px-4 py-3 text-slate-700 dark:text-slate-300"
                                >
                                  {asset.contractor || <span className="text-muted-foreground">-</span>}
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })
                      )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination Footer - Fixed */}
          <div className="border-t bg-card px-6 py-3 flex-shrink-0">
            <div className="flex flex-row flex-nowrap items-center justify-between gap-3 text-sm text-muted-foreground">
              {/* Left: Entry count */}
              <div className="flex-shrink-0">
                Showing {startIndex + 1}-{Math.min(endIndex, sortedAssets.length)} of {sortedAssets.length} entries
                {sortedAssets.length !== assetList.length && ` (filtered from ${assetList.length} total)`}
              </div>
              
              {/* Center: Pagination controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  
                  {/* Page numbers */}
                  <div className="flex items-center gap-1">
                    {generatePageNumbers(currentPage, totalPages).map((page, index) => {
                      if (page === '...') {
                        return (
                          <span key={`ellipsis-${index}`} className="px-2 text-muted-foreground">
                            ...
                          </span>
                        );
                      }
                      return (
                        <Button
                          key={`page-${page}`}
                          variant={currentPage === page ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setCurrentPage(page as number)}
                          className="min-w-[36px]"
                        >
                          {page}
                        </Button>
                      );
                    })}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    Last
                  </Button>
                </div>
              )}
            </div>
          </div>
          </>
          )}
        </div>
      </div>

      <RightPanelBase tabs={rightPanelTabs} defaultTab="actions" />

      {/* Compliance Action Panels */}
      <RecordInspectionPanel
        open={activeModal.type === 'record'}
        onOpenChange={(open) => !open && setActiveModal({ type: null, item: null })}
        complianceItem={activeModal.item ? {
          id: activeModal.item.id ?? 0,
          assetId: activeModal.item.assetId,
          assetName: activeModal.item.assetName,
          assetCode: activeModal.item.assetCode,
          regimeCode: activeModal.item.regimeCode,
          regimeName: activeModal.item.regimeName,
          complianceCheckId: activeModal.item.complianceCheckId,
          frequency: undefined,
        } : undefined}
        onSubmit={async (data) => {
          if (!activeModal.item) return;
          // Transform panel output to hook input format
          const outcomeMap: Record<string, 'Pass' | 'Fail' | 'PassWithAdvisory' | 'ConditionalPass'> = {
            'Pass': 'Pass',
            'Fail': 'Fail',
            'PassWithAdvisories': 'PassWithAdvisory',
          };
          await recordInspection.mutateAsync({
            assetComplianceId: activeModal.item.id ?? 0,
            inspectionDate: data.inspectionDate,
            inspectorName: data.inspectorName,
            outcome: outcomeMap[data.outcome] ?? 'Pass',
            notes: data.findings,
            nextInspectionDate: data.nextInspectionDate,
            files: data.evidenceFiles ?? [],
          });
          setActiveModal({ type: null, item: null });
        }}
      />

      <ScheduleInspectionPanel
        open={activeModal.type === 'schedule'}
        onOpenChange={(open) => !open && setActiveModal({ type: null, item: null })}
        complianceItem={activeModal.item ? {
          id: activeModal.item.id ?? 0,
          assetId: activeModal.item.assetId,
          assetName: activeModal.item.assetName,
          assetCode: activeModal.item.assetCode,
          regimeCode: activeModal.item.regimeCode,
          regimeName: activeModal.item.regimeName,
          complianceCheckId: activeModal.item.complianceCheckId,
          dueDate: activeModal.item.nextDueDate,
        } : undefined}
        onSubmit={async (data) => {
          if (!activeModal.item) return;
          // Transform panel output to hook input format
          await scheduleInspection.mutateAsync({
            assetComplianceId: activeModal.item.id ?? 0,
            newInspectionDate: data.scheduledDate,
            assignedInspector: data.assignedTo,
            schedulingNotes: data.notes,
            sendNotification: false,
          });
          setActiveModal({ type: null, item: null });
        }}
      />

      <MarkNonApplicablePanel
        open={activeModal.type === 'nonApplicable'}
        onOpenChange={(open) => !open && setActiveModal({ type: null, item: null })}
        complianceItem={activeModal.item ? {
          id: activeModal.item.id ?? 0,
          assetId: activeModal.item.assetId,
          assetName: activeModal.item.assetName,
          assetCode: activeModal.item.assetCode,
          regimeCode: activeModal.item.regimeCode,
          regimeName: activeModal.item.regimeName,
          complianceCheckId: activeModal.item.complianceCheckId,
        } : undefined}
        onSubmit={async (data) => {
          if (!activeModal.item) return;
          // Transform panel output to hook input format
          await markNonApplicable.mutateAsync({
            assetComplianceId: activeModal.item.id ?? 0,
            reason: data.reason,
            detailedExplanation: data.notes,
            effectiveDate: data.effectiveDate,
            expiryDate: data.expiryDate,
            files: [],
          });
          setActiveModal({ type: null, item: null });
        }}
      />

      <DeferDueDatePanel
        open={activeModal.type === 'defer'}
        onOpenChange={(open) => !open && setActiveModal({ type: null, item: null })}
        complianceItem={activeModal.item ? {
          id: activeModal.item.id ?? 0,
          assetId: activeModal.item.assetId,
          assetName: activeModal.item.assetName,
          assetCode: activeModal.item.assetCode,
          regimeCode: activeModal.item.regimeCode,
          regimeName: activeModal.item.regimeName,
          complianceCheckId: activeModal.item.complianceCheckId,
          currentDueDate: activeModal.item.nextDueDate ?? new Date().toISOString(),
        } : undefined}
        onSubmit={async (data) => {
          if (!activeModal.item) return;
          // Transform panel output to hook input format
          await deferDueDate.mutateAsync({
            assetComplianceId: activeModal.item.id ?? 0,
            currentDueDate: activeModal.item.nextDueDate ?? new Date().toISOString(),
            newDueDate: data.newDueDate,
            reason: data.reason,
            approvalRequired: false,
          });
          setActiveModal({ type: null, item: null });
        }}
      />

      <ComplianceHistoryPanel
        open={activeModal.type === 'history'}
        onOpenChange={(open) => !open && setActiveModal({ type: null, item: null })}
        complianceItem={activeModal.item ? {
          id: activeModal.item.id ?? 0,
          assetId: activeModal.item.assetId,
          assetName: activeModal.item.assetName,
          assetCode: activeModal.item.assetCode,
          regimeCode: activeModal.item.regimeCode,
          regimeName: activeModal.item.regimeName,
          complianceCheckId: activeModal.item.complianceCheckId,
        } : undefined}
        onExport={(format) => {
          console.log('Export history as', format);
        }}
      />
    </PageWithRightPanel>
  );
}
